<template>
	<!--
	<div
		class="container padding-top-lg-2 padding-bottom-xs-2 padding-bottom-lg-0"
	>
    -->
	<div class="container padding-top-xs-2 padding-bottom-xs-2">
		<div class="row">
			<div class="col-xs-12">
				<div class="row">
					<div class="col-xs-12 col-lg-6">
						<div class="search-activity">
							<input
								type="text"
								class="search-activity-input"
								:class="{
									'nk-force-disable': loading.doingBooking,
									'nk-input-disabled': !cities.selected,
								}"
								:disabled="!cities.selected"
								name="search-activity"
								placeholder="Sök behandling eller konsultation"
								v-model="searchQuery"
								@keyup="handleActivitySearch"
							/>
							<a
								href="#"
								class="clear-search-query link-close-right"
								v-if="searchQuery"
								@click.prevent="clearSearchQuery"
							></a>
						</div>
					</div>
				</div>
				<div
					class="search-activity-results padding-top-xs-2"
					v-if="searchResults.length || searchQuery.length > 0"
				>
					<p v-if="!cities.selected && searchQuery.length >= minimumQueryLength">
						Välj stad först för att kunna söka bland bokningsbara tjänster.
					</p>
					<p v-if="isCityFilterPending && searchQuery.length >= minimumQueryLength">
						Laddar bokningsbara tjänster för vald stad…
					</p>
					<p
						v-if="
							!searchResults.length &&
							cities.selected &&
							!isCityFilterPending &&
							searchQuery.length >= minimumQueryLength
						"
					>
						Vi kunde inte hitta några bokningsbara tjänster som
						matchar din sökning.
					</p>
					<p
						v-if="
							!searchResults.length &&
							searchQuery.length &&
							searchQuery.length < minimumQueryLength
						"
					>
						Skriv minst {{ minimumQueryLength }} tecken för att söka
						efter en tjänst.
					</p>
					<div v-if="searchResults.length">
						<ul
							class="nk-list nk-list-two-columns-md fade-on-hover"
						>
							<li
								v-for="activity in searchResults"
								:key="activity.id"
							>
								<a
									href="#"
									class="force-icon-right"
									@click="handleSelectActivity(activity)"
								>
									<span v-html="activity.activity_name"></span
								></a>
							</li>
						</ul>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";

export default {
	components: {},
	data() {
		return {
			searchQuery: "",
			searchResults: [],
			minimumQueryLength: 3,
			isCityFilterPending: false,
			searchRunId: 0,
		};
	},
	computed: {
		...mapState(["activities", "activityGroups", "cities"]),
		...mapGetters(["loading"]),
	},
	methods: {
		...mapActions([
			"setActivity",
			"setModalState",
			"setActivityGroupFromSearchResult",
			"filterActivitiesByCityAvailability",
		]),
		clearSearchQuery() {
			this.searchQuery = "";
			this.searchResults = [];
			this.isCityFilterPending = false;
		},
		handleSelectActivity(activity) {
			/* Don't let the user set the activity if we're loading times because this messes up the UI */
			if (this.loading.times) {
				return;
			}
			/* If a BankID sign is currently in progress, don't let the user select a new activity */
			if (this.loading.doingBooking) {
				return;
			}

			this.searchQuery = activity.activity_name;
			this.handleActivitySearch();
			this.setActivity(activity);

			/* Also set the activity group to make the UI work the same as when a user selects the activity group first and then selects the individual activity */
			for (let i in this.activityGroups.available) {
				if (
					this.activityGroups.available[i].activity_group_id ==
					activity.activity_group_id
				) {
					this.setActivityGroupFromSearchResult(
						this.activityGroups.available[i]
					);
					break;
				}
			}

			/* Set the modal state to 3 to make the calendar modal show on mobile */
			this.setModalState(3);
		},
		async handleActivitySearch() {
			const runId = ++this.searchRunId;
			let results = [];
			let searchQuery = this.searchQuery.toLowerCase();

			// When a group is selected, the UI already shows a filtered list (activities.active).
			// Search should match exactly what the user sees there (no extra loading / no later removal).
			const hasSelectedGroup =
				this.activityGroups && this.activityGroups.selected;
			const sourceActivities =
				hasSelectedGroup && this.activities && Array.isArray(this.activities.active)
					? this.activities.active
					: this.activities.available;

			// City-level filtering should primarily use cached **group_ids** (fast, stable, and matches the UI).
			// This avoids relying on activity_ids (which may be missing/stale) and avoids showing wrong results first.
			const hasCitySelected = this.cities && this.cities.selected;
			const allowedGroupIds =
				hasCitySelected &&
				this.activityGroups &&
				Array.isArray(this.activityGroups.cityBookableGroupIds) &&
				this.activityGroups.cityBookableGroupIds.length
					? new Set(this.activityGroups.cityBookableGroupIds.map((x) => String(x)))
					: null;

			/* Only do the search if the query is longer than x characters */
			if (searchQuery.length >= this.minimumQueryLength) {
				for (let i in sourceActivities) {
					let activity = sourceActivities[i];
					// Skip invalid/inactive activities (match group list behavior)
					if (!activity || !activity.activity_name) continue;
					if (activity.activity_active && String(activity.activity_active) !== "1") continue;
					if (activity.activity_hide_in_patient_portal && String(activity.activity_hide_in_patient_portal) === "1") continue;
					// If no group selected, and we have cached city group_ids, constrain search to those groups.
					if (!hasSelectedGroup && allowedGroupIds && !allowedGroupIds.has(String(activity.activity_group_id))) {
						continue;
					}
					let activityName = activity.activity_name.toLowerCase();
					if (activityName.includes(searchQuery)) {
						results.push(activity);
					}
				}
			}

			// If a group is selected, never block search on cityBookableIds – activities.active is the truth.
			if (hasSelectedGroup) {
				if (runId !== this.searchRunId) return;
				this.isCityFilterPending = false;
				this.searchResults = results;
				return;
			}

			// If a city is selected and we're doing global search (no group selected), we require city group_ids.
			// If they aren't ready yet, show the loading hint (but this should be brief and matches group rendering).
			if (!hasSelectedGroup && hasCitySelected && searchQuery.length >= this.minimumQueryLength) {
				if (!allowedGroupIds) {
					this.isCityFilterPending = true;
					this.searchResults = [];
					return;
				}
				this.isCityFilterPending = false;
				this.searchResults = results;
				return;
			}

			// No city selected (or query too short): regular search
			if (runId !== this.searchRunId) return;
			this.isCityFilterPending = false;
			this.searchResults = results;
		},
	},
	watch: {
		// When group selection changes, rerun search and never keep the "loading for city" message around.
		// This fixes the case where the user typed first (pending=true) and then selected a group.
		"activityGroups.selected"() {
			this.isCityFilterPending = false;
			this.handleActivitySearch();
		},
		// If the user types before activities have loaded, rerun once they arrive so results populate.
		"activities.loaded"() {
			if (this.searchQuery && this.searchQuery.length >= this.minimumQueryLength) {
				this.handleActivitySearch();
			}
		},
		// When city changes or the precomputed ids arrive, rerun search so results become correct.
		"cities.selected"() {
			this.handleActivitySearch();
		},
		"activities.cityBookableIds"() {
			if (this.searchQuery && this.searchQuery.length >= this.minimumQueryLength) {
				this.handleActivitySearch();
			}
		},
		"activityGroups.cityBookableGroupIds"() {
			if (this.searchQuery && this.searchQuery.length >= this.minimumQueryLength) {
				this.handleActivitySearch();
			}
		},
	},
};
</script>
				this.activityGroups &&
				Array.isArray(this.activityGroups.cityBookableGroupIds) &&
				this.activityGroups.cityBookableGroupIds.length
					? new Set(this.activityGroups.cityBookableGroupIds.map((x) => String(x)))
					: null;

			/* Only do the search if the query is longer than x characters */
			if (searchQuery.length >= this.minimumQueryLength) {
				for (let i in sourceActivities) {
					let activity = sourceActivities[i];
					// Skip invalid/inactive activities (match group list behavior)
					if (!activity || !activity.activity_name) continue;
					if (activity.activity_active && String(activity.activity_active) !== "1") continue;
					if (activity.activity_hide_in_patient_portal && String(activity.activity_hide_in_patient_portal) === "1") continue;
					// If no group selected, and we have cached city group_ids, constrain search to those groups.
					if (!hasSelectedGroup && allowedGroupIds && !allowedGroupIds.has(String(activity.activity_group_id))) {
						continue;
					}
					let activityName = activity.activity_name.toLowerCase();
					if (activityName.includes(searchQuery)) {
						results.push(activity);
					}
				}
			}

			// If a group is selected, never block search on cityBookableIds – activities.active is the truth.
			if (hasSelectedGroup) {
				if (runId !== this.searchRunId) return;
				this.isCityFilterPending = false;
				this.searchResults = results;
				return;
			}

			// If a city is selected and we're doing global search (no group selected), we require city group_ids.
			// If they aren't ready yet, show the loading hint (but this should be brief and matches group rendering).
			if (!hasSelectedGroup && hasCitySelected && searchQuery.length >= this.minimumQueryLength) {
				if (!allowedGroupIds) {
					this.isCityFilterPending = true;
					this.searchResults = [];
					return;
				}
				this.isCityFilterPending = false;
				this.searchResults = results;
				return;
			}

			// No city selected (or query too short): regular search
			if (runId !== this.searchRunId) return;
			this.isCityFilterPending = false;
			this.searchResults = results;
		},
	},
	watch: {
		// When group selection changes, rerun search and never keep the "loading for city" message around.
		// This fixes the case where the user typed first (pending=true) and then selected a group.
		"activityGroups.selected"() {
			this.isCityFilterPending = false;
			this.handleActivitySearch();
		},
		// If the user types before activities have loaded, rerun once they arrive so results populate.
		"activities.loaded"() {
			if (this.searchQuery && this.searchQuery.length >= this.minimumQueryLength) {
				this.handleActivitySearch();
			}
		},
		// When city changes or the precomputed ids arrive, rerun search so results become correct.
		"cities.selected"() {
			this.handleActivitySearch();
		},
		"activities.cityBookableIds"() {
			if (this.searchQuery && this.searchQuery.length >= this.minimumQueryLength) {
				this.handleActivitySearch();
			}
		},
		"activityGroups.cityBookableGroupIds"() {
			if (this.searchQuery && this.searchQuery.length >= this.minimumQueryLength) {
				this.handleActivitySearch();
			}
		},
	},
};
</script>